/**
 * Web Worker bridge for MiniLM-L6-v2 embeddings.
 * All inference runs off the main thread in a dedicated Worker.
 * Zero network calls after initial model download (cached in browser Cache API).
 */

import type { EmbeddingWorkerMessage } from '../types/index';

let worker: Worker | null = null;
let ready = false;
let initPromise: Promise<void> | null = null;

// ---------------------------------------------------------------------------
// Pending request tracking
// ---------------------------------------------------------------------------

interface PendingRequest {
  resolve: (vector: Float32Array) => void;
  reject: (error: Error) => void;
}

/**
 * Queue of pending embed requests.
 * The worker processes messages sequentially, so we use a simple FIFO queue.
 */
const pendingQueue: PendingRequest[] = [];

/**
 * Single-use resolve/reject for the init handshake.
 */
let initResolve: (() => void) | null = null;
let initReject: ((err: Error) => void) | null = null;

// ---------------------------------------------------------------------------
// Worker message handler
// ---------------------------------------------------------------------------

function handleWorkerMessage(event: MessageEvent<EmbeddingWorkerMessage>): void {
  const msg = event.data;

  switch (msg.type) {
    case 'ready': {
      ready = true;
      if (initResolve) {
        initResolve();
        initResolve = null;
        initReject = null;
      }
      break;
    }

    case 'result': {
      const pending = pendingQueue.shift();
      if (pending && msg.vector) {
        pending.resolve(new Float32Array(msg.vector));
      } else if (pending) {
        pending.reject(new Error('Worker returned result without vector data'));
      }
      break;
    }

    case 'error': {
      // If we are still initializing, reject the init promise.
      if (!ready && initReject) {
        initReject(new Error(msg.error ?? 'Unknown worker error during init'));
        initResolve = null;
        initReject = null;
        return;
      }

      // Otherwise reject the oldest pending embed request.
      const pending = pendingQueue.shift();
      if (pending) {
        pending.reject(new Error(msg.error ?? 'Unknown worker error'));
      }
      break;
    }

    default:
      break;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize the embedding engine.
 * Creates the Web Worker and waits for the MiniLM-L6-v2 model to load.
 * Safe to call multiple times -- subsequent calls return immediately.
 */
export async function init(): Promise<void> {
  if (ready) return;
  if (initPromise) return initPromise;

  initPromise = new Promise<void>((resolve, reject) => {
    initResolve = resolve;
    initReject = reject;

    worker = new Worker(
      new URL('./embedding-worker.ts', import.meta.url),
      { type: 'module' },
    );

    worker.onmessage = handleWorkerMessage;

    worker.onerror = (err) => {
      if (initReject) {
        initReject(new Error(`Worker load error: ${err.message}`));
        initResolve = null;
        initReject = null;
      }
    };

    const msg: EmbeddingWorkerMessage = { type: 'init' };
    worker.postMessage(msg);
  });

  return initPromise;
}

/**
 * Generate a 384-dimensional embedding for the given text.
 * The init() method must be called (and awaited) before using this.
 */
export async function embed(text: string): Promise<Float32Array> {
  if (!worker || !ready) {
    throw new Error(
      'Embedding engine not initialized. Call init() and await it first.',
    );
  }

  return new Promise<Float32Array>((resolve, reject) => {
    pendingQueue.push({ resolve, reject });
    const msg: EmbeddingWorkerMessage = { type: 'embed', text };
    worker!.postMessage(msg);
  });
}

/**
 * Compute cosine similarity between two vectors.
 * Returns a value between -1 and 1 (1 = identical direction).
 */
export function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return 0;

  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }

  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : dot / denom;
}
